import * as React from 'react';

import { ReportTypes } from '@showbie-socrative/socrative-utils';

import { PaywallScreens } from '~analytics/types';
import { useTeacherApplicationContext } from '~shared/context/TeacherApplicationContext';
import { ReportTypeContext } from '~teacher/components/ExportReports/ReportTypeContext';
import { usePaywallContext } from '~teacher/components/PaywallDialog';
import { useReportAnalytics } from '~teacher/components/QuizResultsHeader/ReportAnalyticsContext';

import {
  downloadReports,
  emailReportsToTeacher,
  uploadToDrive,
} from '../../../api/reports/reportsApi';
import { ExportFeedbackDefaultError } from './ExportFeedback/ExportFeedback';
import {
  ExportFeedbackDownloadComplete,
  ExportFeedbackDownloadInProgress,
} from './ExportFeedback/ExportFeedbackDownload';
import { ExportFeedbackEmailMeComplete } from './ExportFeedback/ExportFeedbackEmailMe';
import { ExportFeedbackSaveToDriveComplete } from './ExportFeedback/ExportFeedbackSaveToDrive';
import { ReportOption, ReportOptionType } from './ReportOption';
import { ReportOptionHeader } from './ReportOption/ReportOptionHeader';
import {
  useOnPrepareDownloadCallback,
  useOnSendEmailToMeCallBack,
  useOnUploadToDriveCallback,
  useReportReducer,
} from './ReportOption/ReportOptionHook';
import {
  ExportView,
  ReportOptionState,
  toForm,
} from './ReportOption/ReportOptionHookReducer';

export interface CommonProps {
  activityInstanceId: number;
  initialOptions: ReportOptionState;
  isSaveToDriveDisabled: boolean;
  downloadReports: typeof downloadReports;
  uploadToDrive: typeof uploadToDrive;
  emailReportsToTeacher: typeof emailReportsToTeacher;
  canCustomizeExportResults?: boolean;
}

interface ReportSpecificProps {
  reportOptions: ReportOptionType[];
  initialOptions: ReportOptionState;
  reportRequestSelectedType: ReportTypes;
  description: string;
  title: string;
  shouldShowPaywallOnReportsButtonClicked: boolean;
  shouldShowProBadge: boolean;
  shouldShowProBadgeInHeader: boolean;
}

type Props = CommonProps & ReportSpecificProps;

export function ExportReportPanel({
  activityInstanceId,
  isSaveToDriveDisabled,
  downloadReports,
  uploadToDrive,
  emailReportsToTeacher,
  reportOptions,
  initialOptions,
  reportRequestSelectedType,
  description,
  title,
  shouldShowPaywallOnReportsButtonClicked,
  shouldShowProBadge,
  shouldShowProBadgeInHeader,
}: Props) {
  const { dispatch, state } = useReportReducer({
    reportOptions,
    initialOptions,
  });
  const onBackToOptionSettings = () => toForm(dispatch)();
  const { dispatch: paywallDispatch } = usePaywallContext();
  const paywallScreen =
    reportRequestSelectedType === ReportTypes.EmptyQuiz
      ? PaywallScreens.ReportAnswerKey
      : null;
  const reportAnalytics = useReportAnalytics();
  const { exportView, optionState, error, isFetching } = state;
  const appContext = useTeacherApplicationContext();

  const prepareDownload = useOnPrepareDownloadCallback({
    activityInstanceId,
    reportAnalytics,
    reportRequestSelectedType,
    optionState,
    dispatch,
    appContext,
    downloadReports,
  });

  const handlePrepareDownload = React.useCallback(() => {
    shouldShowPaywallOnReportsButtonClicked
      ? paywallDispatch({ type: 'OPEN', touch: [paywallScreen] })
      : prepareDownload();
  }, [
    shouldShowPaywallOnReportsButtonClicked,
    paywallDispatch,
    paywallScreen,
    prepareDownload,
  ]);

  const sendEmailToMe = useOnSendEmailToMeCallBack({
    activityInstanceId,
    reportAnalytics,
    reportRequestSelectedType,
    optionState,
    dispatch,
    emailReportsToTeacher,
  });

  const handleSendEmailToMe = React.useCallback(() => {
    shouldShowPaywallOnReportsButtonClicked
      ? paywallDispatch({ type: 'OPEN', touch: [paywallScreen] })
      : sendEmailToMe();
  }, [
    shouldShowPaywallOnReportsButtonClicked,
    paywallDispatch,
    paywallScreen,
    sendEmailToMe,
  ]);

  const startUploadToDrive = useOnUploadToDriveCallback({
    activityInstanceId,
    reportAnalytics,
    reportRequestSelectedType,
    optionState,
    dispatch,
    uploadToDrive,
  });

  const handleUploadToDrive = React.useCallback(() => {
    shouldShowPaywallOnReportsButtonClicked
      ? paywallDispatch({ type: 'OPEN', touch: [paywallScreen] })
      : startUploadToDrive();
  }, [
    shouldShowPaywallOnReportsButtonClicked,
    paywallDispatch,
    paywallScreen,
    startUploadToDrive,
  ]);

  switch (exportView) {
    case ExportView.FORM:
      return (
        <ReportTypeContext.Provider value={reportRequestSelectedType}>
          <ReportOption
            isFetching={isFetching}
            shouldShowProBadge={shouldShowProBadge}
            isSaveToDriveDisabled={isSaveToDriveDisabled}
            onPrepareDownload={handlePrepareDownload}
            onSendEmailToMe={handleSendEmailToMe}
            onUploadToDrive={handleUploadToDrive}
            reportOptions={reportOptions}
            optionState={optionState}
            header={
              <ReportOptionHeader
                description={description}
                title={title}
                shouldShowProBadge={shouldShowProBadgeInHeader}
              />
            }
            dispatch={dispatch}
          />
        </ReportTypeContext.Provider>
      );
    case ExportView.PREPARING:
      return (
        <ExportFeedbackDownloadInProgress onBack={onBackToOptionSettings} />
      );
    case ExportView.DOWNLOADING:
      return <ExportFeedbackDownloadComplete onBack={onBackToOptionSettings} />;
    case ExportView.EMAIL_ME:
      return <ExportFeedbackEmailMeComplete onBack={onBackToOptionSettings} />;
    case ExportView.SAVE_TO_DRIVE:
      return (
        <ExportFeedbackSaveToDriveComplete onBack={onBackToOptionSettings} />
      );
    case ExportView.ERROR:
      return (
        <ExportFeedbackDefaultError
          error={error}
          onBack={onBackToOptionSettings}
        />
      );
    default:
      throw new Error('unreachable code!');
  }
}
